import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { centralAdminApi } from '../lib/centralTenantAdminApi';

const OVPLAN1_ACTIONS = {
  createProject: { label: 'Create Project', defaultCreditCost: 10, isActive: true },
  createBoard: { label: 'Create Board', defaultCreditCost: 5, isActive: true },
  createSprintBoard: { label: 'Create Sprint Board', defaultCreditCost: 5, isActive: true },
  addCard: { label: 'Add Card', defaultCreditCost: 1, isActive: true },
};

const EMPTY_TENANT = {
  tenantId: '',
  tenantName: '',
  planId: '',
  status: 'active',
  billingStatus: 'active',
  monthlyCredits: 0,
  purchasedCredits: 0,
  usedCredits: 0,
  remainingCredits: 0,
  userLimit: 0,
  activeUsers: 0,
  allowedApps: 'ovplan1',
};

const EMPTY_PLAN = {
  planId: '',
  name: '',
  description: '',
  price: 0,
  monthlyCredits: 1000,
  userLimit: 25,
  allowedApps: 'ovplan1',
  isActive: true,
  features: JSON.stringify({ ovplan1: { createProject: true, createBoard: true, createSprintBoard: true, addCard: true } }, null, 2),
  creditCosts: JSON.stringify({ ovplan1: { createProject: 10, createBoard: 5, createSprintBoard: 5, addCard: 1 } }, null, 2),
};

const EMPTY_APP = {
  appId: 'ovplan1',
  name: 'OVplan1',
  description: 'Connected OVplan1 product app.',
  isActive: true,
  actions: JSON.stringify(OVPLAN1_ACTIONS, null, 2),
};

const EMPTY_CLIENT = {
  appId: 'ovplan1',
  name: 'OVplan1 production client',
};

const statusColors = {
  active: 'success',
  inactive: 'default',
  suspended: 'warning',
  revoked: 'error',
  trialing: 'info',
  past_due: 'warning',
  cancelled: 'error',
};

const formatNumber = (value) => Number(value || 0).toLocaleString();

const toDateLabel = (value) => {
  if (!value) return '-';
  const date = value.toDate
    ? value.toDate()
    : new Date((value.seconds || value._seconds) ? (value.seconds || value._seconds) * 1000 : value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
};

const parseList = (value) => String(value || '').split(',').map((item) => item.trim()).filter(Boolean);

const parseJson = (value, label) => {
  try {
    return value ? JSON.parse(value) : {};
  } catch (error) {
    throw new Error(`${label} must be valid JSON.`);
  }
};

const getErrorMessage = (error) => {
  const reason = error?.reason || error?.code || error?.message;
  const messages = {
    INSUFFICIENT_CREDITS: 'Not enough credits for this action.',
    INVALID_TENANT: 'Tenant not found.',
    TENANT_INACTIVE: 'This tenant is inactive.',
    BILLING_INACTIVE: 'Billing is inactive for this tenant.',
    APP_ACCESS_DENIED: 'This tenant does not have access to that app.',
    INVALID_PLAN: 'Invalid pricing plan.',
    INVALID_ACTION: 'Invalid connected app action.',
    ACTION_NOT_ALLOWED: 'This action is not allowed in the selected plan.',
    CLIENT_REVOKED: 'This API client has been revoked.',
    APP_MISMATCH: 'The API client does not match the requested app.',
  };
  return messages[reason] || error?.message || reason || 'Something went wrong.';
};

const TenantPricingConsole = ({ compactHeader = false }) => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [apps, setApps] = useState([]);
  const [creditUsage, setCreditUsage] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [apiClients, setApiClients] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [tenantForm, setTenantForm] = useState(EMPTY_TENANT);
  const [planForm, setPlanForm] = useState(EMPTY_PLAN);
  const [appForm, setAppForm] = useState(EMPTY_APP);
  const [clientForm, setClientForm] = useState(EMPTY_CLIENT);
  const [creditAmount, setCreditAmount] = useState(100);
  const [manualDeductAmount, setManualDeductAmount] = useState(10);
  const [newClientSecret, setNewClientSecret] = useState('');
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState({ open: false, severity: 'success', message: '' });

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => tenant.tenantId === selectedTenantId) || tenants[0] || null,
    [selectedTenantId, tenants]
  );

  const showNotice = (message, severity = 'success') => {
    setNotice({ open: true, severity, message });
  };

  const refresh = async () => {
    setLoading(true);
    try {
      setLoadError('');
      const [dashboardData, tenantData, planData, appData, usageData, auditData, clientData] = await Promise.all([
        centralAdminApi.getDashboard(),
        centralAdminApi.listTenants(),
        centralAdminApi.listPricingPlans(),
        centralAdminApi.listApps(),
        centralAdminApi.listCreditUsage({ limit: 100 }),
        centralAdminApi.listAuditLogs({ limit: 100 }),
        centralAdminApi.listApiClients(),
      ]);

      const nextTenants = tenantData.tenants || [];
      setDashboard(dashboardData);
      setTenants(nextTenants);
      setPlans(planData.plans || []);
      setApps(appData.apps || []);
      setCreditUsage(usageData.usage || []);
      setAuditLogs(auditData.logs || []);
      setApiClients(clientData.clients || []);
      setSelectedTenantId((current) => (
        nextTenants.some((tenant) => tenant.tenantId === current)
          ? current
          : nextTenants[0]?.tenantId || ''
      ));
    } catch (error) {
      console.error('Central Tenant Management load failed:', error);
      setLoadError('Could not load data. Check Firestore permissions or configuration.');
      showNotice(getErrorMessage(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const withSave = async (operation, successMessage) => {
    setSaving(true);
    try {
      const result = await operation();
      if (result?.success === false) {
        showNotice(getErrorMessage(result), 'error');
        return result;
      }
      showNotice(successMessage);
      await refresh();
      return result;
    } catch (error) {
      showNotice(getErrorMessage(error), 'error');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const tenantPayload = () => ({
    ...tenantForm,
    monthlyCredits: Number(tenantForm.monthlyCredits || 0),
    purchasedCredits: Number(tenantForm.purchasedCredits || 0),
    usedCredits: Number(tenantForm.usedCredits || 0),
    remainingCredits: Number(tenantForm.remainingCredits || 0),
    userLimit: Number(tenantForm.userLimit || 0),
    activeUsers: Number(tenantForm.activeUsers || 0),
    allowedApps: parseList(tenantForm.allowedApps),
  });

  const planPayload = () => ({
    ...planForm,
    price: Number(planForm.price || 0),
    monthlyCredits: Number(planForm.monthlyCredits || 0),
    userLimit: Number(planForm.userLimit || 0),
    allowedApps: parseList(planForm.allowedApps),
    isActive: Boolean(planForm.isActive),
    features: parseJson(planForm.features, 'Plan features'),
    creditCosts: parseJson(planForm.creditCosts, 'Plan credit costs'),
  });

  const appPayload = () => ({
    ...appForm,
    isActive: Boolean(appForm.isActive),
    actions: parseJson(appForm.actions, 'App actions'),
  });

  const handleCreateTenant = () => withSave(async () => {
    const result = await centralAdminApi.createTenant(tenantPayload());
    setTenantForm(EMPTY_TENANT);
    return result;
  }, 'Tenant created in central source of truth.');

  const handleUpdateTenant = () => {
    if (!selectedTenant) return null;
    return withSave(
      async () => centralAdminApi.updateTenant({ ...selectedTenant, ...tenantPayload(), tenantId: selectedTenant.tenantId }),
      'Tenant updated.'
    );
  };

  const handleEditTenant = (tenant) => {
    setSelectedTenantId(tenant.tenantId);
    setTenantForm({
      tenantId: tenant.tenantId || '',
      tenantName: tenant.tenantName || '',
      planId: tenant.planId || '',
      status: tenant.status || 'active',
      billingStatus: tenant.billingStatus || 'active',
      monthlyCredits: tenant.monthlyCredits || 0,
      purchasedCredits: tenant.purchasedCredits || 0,
      usedCredits: tenant.usedCredits || 0,
      remainingCredits: tenant.remainingCredits || 0,
      userLimit: tenant.userLimit || 0,
      activeUsers: tenant.activeUsers || 0,
      allowedApps: (tenant.allowedApps || []).join(', '),
    });
  };

  const handleSavePlan = () => withSave(async () => {
    const payload = planPayload();
    const exists = plans.some((plan) => plan.planId === payload.planId);
    const result = exists
      ? await centralAdminApi.updatePricingPlan(payload)
      : await centralAdminApi.createPricingPlan(payload);
    setPlanForm(EMPTY_PLAN);
    return result;
  }, 'Pricing plan saved.');

  const handleEditPlan = (plan) => {
    setPlanForm({
      planId: plan.planId || '',
      name: plan.name || '',
      description: plan.description || '',
      price: plan.price || 0,
      monthlyCredits: plan.monthlyCredits || 0,
      userLimit: plan.userLimit || 0,
      allowedApps: (plan.allowedApps || []).join(', '),
      isActive: plan.isActive !== false,
      features: JSON.stringify(plan.features || {}, null, 2),
      creditCosts: JSON.stringify(plan.creditCosts || {}, null, 2),
    });
  };

  const handleSaveApp = () => withSave(async () => {
    const payload = appPayload();
    const exists = apps.some((app) => app.appId === payload.appId);
    const result = exists
      ? await centralAdminApi.updateApp(payload)
      : await centralAdminApi.createApp(payload);
    setAppForm(EMPTY_APP);
    return result;
  }, 'Connected app saved.');

  const handleEditApp = (app) => {
    setAppForm({
      appId: app.appId || '',
      name: app.name || '',
      description: app.description || '',
      isActive: app.isActive !== false,
      actions: JSON.stringify(app.actions || {}, null, 2),
    });
  };

  const handleCreateApiClient = () => withSave(async () => {
    const result = await centralAdminApi.createApiClient(clientForm);
    if (result?.clientSecret) {
      setNewClientSecret(result.clientSecret);
    }
    setClientForm(EMPTY_CLIENT);
    return result;
  }, 'API client created. Copy the secret now; it will not be shown again.');

  const summary = dashboard?.summary || {};
  const activeApps = apps.filter((app) => app.isActive !== false);

  return (
    <Box sx={{ minHeight: compactHeader ? 'auto' : '100vh', bgcolor: '#eef5f8', p: { xs: 2, md: 4 } }}>
      {!compactHeader && (
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="overline" sx={{ color: '#1f6f8b', fontWeight: 800, letterSpacing: 2 }}>
              Central Tenant Management
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 900, color: '#132331' }}>
              Source of truth console
            </Typography>
            <Typography sx={{ color: '#607083', mt: 1 }}>
              Manage tenants, pricing, app access, credits, API clients, and audit history for connected apps.
            </Typography>
          </Box>
          <Button variant="contained" onClick={refresh} disabled={loading || saving}>
            Refresh
          </Button>
        </Stack>
      )}

      {compactHeader && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
          <Button variant="contained" onClick={refresh} disabled={loading || saving}>
            Refresh Data
          </Button>
        </Stack>
      )}

      {loadError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {loadError} Showing safe empty states instead of a blank page.
        </Alert>
      )}

      <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #d9e5ec' }}>
        <Tabs
          value={tab}
          onChange={(_, nextTab) => setTab(nextTab)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ bgcolor: '#ffffff', borderBottom: '1px solid #e1ebf0' }}
        >
          <Tab label="Dashboard" />
          <Tab label="Tenants" />
          <Tab label="Pricing Plans" />
          <Tab label="Apps" />
          <Tab label="Credit Usage" />
          <Tab label="API Clients" />
          <Tab label="Audit Logs" />
        </Tabs>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {loading ? (
            <Stack alignItems="center" sx={{ py: 8 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2, color: '#607083' }}>Loading central tenant data...</Typography>
            </Stack>
          ) : (
            <>
              {tab === 0 && (
                <Stack spacing={3}>
                  <Grid container spacing={2}>
                    <SummaryCard label="Total tenants" value={summary.totalTenants} />
                    <SummaryCard label="Active tenants" value={summary.activeTenants} />
                    <SummaryCard label="Suspended tenants" value={summary.suspendedTenants} />
                    <SummaryCard label="Credits used" value={summary.totalCreditsUsed} />
                    <SummaryCard label="Remaining credits" value={summary.totalRemainingCredits} />
                    <SummaryCard label="Pricing plans" value={summary.totalPricingPlans} />
                    <SummaryCard label="Connected apps" value={apps.length} />
                    <SummaryCard label="API clients" value={apiClients.length} />
                  </Grid>
                  <Section title="Quick actions">
                    <Grid container spacing={2}>
                      <QuickAction title="Create tenant" description="Add a central tenant/license account." onClick={() => setTab(1)} />
                      <QuickAction title="Create pricing plan" description="Define credits, user limits, app access, and costs." onClick={() => setTab(2)} />
                      <QuickAction title="Add connected app" description="Register ovplan1 or any future app." onClick={() => setTab(3)} />
                      <QuickAction title="Create API client" description="Generate client credentials for REST access." onClick={() => setTab(5)} />
                    </Grid>
                  </Section>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Section title="Recent credit usage">
                        <CreditUsageTable rows={dashboard?.recentCreditUsage || []} compact />
                      </Section>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Section title="Recent audit logs">
                        <AuditTable rows={dashboard?.recentAuditLogs || []} compact />
                      </Section>
                    </Grid>
                  </Grid>
                </Stack>
              )}

              {tab === 1 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} lg={4}>
                    <Section title="Create / edit tenant">
                      <TenantForm
                        form={tenantForm}
                        setForm={setTenantForm}
                        plans={plans}
                        apps={activeApps}
                        selectedTenant={selectedTenant}
                        onCreate={handleCreateTenant}
                        onUpdate={handleUpdateTenant}
                        saving={saving}
                      />
                    </Section>
                    <Section title="Selected tenant controls" sx={{ mt: 2 }}>
                      <TenantControls
                        tenant={selectedTenant}
                        plans={plans}
                        apps={activeApps}
                        creditAmount={creditAmount}
                        setCreditAmount={setCreditAmount}
                        manualDeductAmount={manualDeductAmount}
                        setManualDeductAmount={setManualDeductAmount}
                        saving={saving}
                        onAssignPlan={(planId) => withSave(
                          () => centralAdminApi.assignPlanToTenant({ tenantId: selectedTenant.tenantId, planId }),
                          'Pricing plan assigned.'
                        )}
                        onStatus={(status) => withSave(
                          () => centralAdminApi.updateTenantStatus({ tenantId: selectedTenant.tenantId, status }),
                          'Tenant status updated.'
                        )}
                        onBilling={(billingStatus) => withSave(
                          () => centralAdminApi.updateTenantBillingStatus({ tenantId: selectedTenant.tenantId, billingStatus }),
                          'Billing status updated.'
                        )}
                        onAllowedApps={(allowedApps) => withSave(
                          () => centralAdminApi.updateTenantAllowedApps({ tenantId: selectedTenant.tenantId, allowedApps }),
                          'Allowed apps updated.'
                        )}
                        onUserLimit={(userLimit) => withSave(
                          () => centralAdminApi.updateTenantUserLimit({ tenantId: selectedTenant.tenantId, userLimit }),
                          'User limit updated.'
                        )}
                        onTopUp={() => withSave(
                          () => centralAdminApi.addCreditsToTenant({ tenantId: selectedTenant.tenantId, amount: Number(creditAmount || 0) }),
                          'Credits added.'
                        )}
                        onDeduct={() => withSave(
                          () => centralAdminApi.manualDeductCreditsFromTenant({ tenantId: selectedTenant.tenantId, amount: Number(manualDeductAmount || 0) }),
                          'Credits deducted manually.'
                        )}
                      />
                    </Section>
                  </Grid>
                  <Grid item xs={12} lg={8}>
                    <Section title="Tenants">
                      <TenantTable rows={tenants} onEdit={handleEditTenant} />
                    </Section>
                  </Grid>
                </Grid>
              )}

              {tab === 2 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} lg={4}>
                    <Section title="Pricing plan editor">
                      <PlanForm form={planForm} setForm={setPlanForm} apps={activeApps} onSave={handleSavePlan} saving={saving} />
                    </Section>
                  </Grid>
                  <Grid item xs={12} lg={8}>
                    <Section title="Pricing plans">
                      <PricingPlanTable
                        rows={plans}
                        onEdit={handleEditPlan}
                        onStatus={(plan) => withSave(
                          () => centralAdminApi.setPricingPlanStatus({ planId: plan.planId, isActive: plan.isActive === false }),
                          'Pricing plan status updated.'
                        )}
                      />
                    </Section>
                  </Grid>
                </Grid>
              )}

              {tab === 3 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} lg={4}>
                    <Section title="Connected app editor">
                      <AppForm form={appForm} setForm={setAppForm} onSave={handleSaveApp} saving={saving} />
                    </Section>
                  </Grid>
                  <Grid item xs={12} lg={8}>
                    <Section title="Connected apps">
                      <AppsTable
                        rows={apps}
                        onEdit={handleEditApp}
                        onStatus={(app) => withSave(
                          () => centralAdminApi.setAppStatus({ appId: app.appId, isActive: app.isActive === false }),
                          'Connected app status updated.'
                        )}
                      />
                    </Section>
                  </Grid>
                </Grid>
              )}

              {tab === 4 && (
                <Section title="Credit usage ledger">
                  <CreditUsageTable rows={creditUsage} />
                </Section>
              )}

              {tab === 5 && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Section title="Create API client">
                      <Stack spacing={2}>
                        <TextField
                          label="Client name"
                          value={clientForm.name}
                          onChange={(event) => setClientForm({ ...clientForm, name: event.target.value })}
                          fullWidth
                        />
                        <FormControl fullWidth>
                          <InputLabel>App</InputLabel>
                          <Select
                            label="App"
                            value={clientForm.appId}
                            onChange={(event) => setClientForm({ ...clientForm, appId: event.target.value })}
                          >
                            {apps.map((app) => (
                              <MenuItem value={app.appId} key={app.appId}>{app.name} ({app.appId})</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button variant="contained" disabled={saving} onClick={handleCreateApiClient}>
                          Generate Client Secret
                        </Button>
                        {newClientSecret && (
                          <Alert severity="warning">
                            Copy this secret now. It is shown only once:
                            <Box component="code" sx={{ display: 'block', mt: 1, wordBreak: 'break-all' }}>
                              {newClientSecret}
                            </Box>
                          </Alert>
                        )}
                      </Stack>
                    </Section>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Section title="API clients and integration settings">
                      <ApiClientsTable
                        rows={apiClients}
                        onRevoke={(client) => withSave(
                          () => centralAdminApi.revokeApiClient({ clientId: client.clientId }),
                          'API client revoked.'
                        )}
                      />
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Future apps call REST APIs with X-Client-Id and X-Client-Secret headers. Raw secrets are never stored or shown again.
                      </Alert>
                    </Section>
                  </Grid>
                </Grid>
              )}

              {tab === 6 && (
                <Section title="Audit logs">
                  <AuditTable rows={auditLogs} />
                </Section>
              )}
            </>
          )}
        </Box>
      </Paper>

      <Snackbar
        open={notice.open}
        autoHideDuration={4500}
        onClose={() => setNotice({ ...notice, open: false })}
      >
        <Alert severity={notice.severity} onClose={() => setNotice({ ...notice, open: false })}>
          {notice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const Section = ({ title, children, sx }) => (
  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #dbe7ee', ...sx }}>
    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>{title}</Typography>
    {children}
  </Paper>
);

const SummaryCard = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4} lg={2}>
    <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #dbe7ee' }}>
      <CardContent>
        <Typography sx={{ color: '#607083', fontWeight: 700 }}>{label}</Typography>
        <Typography variant="h4" sx={{ fontWeight: 900, color: '#132331', mt: 1 }}>
          {formatNumber(value)}
        </Typography>
      </CardContent>
    </Card>
  </Grid>
);

const QuickAction = ({ title, description, onClick }) => (
  <Grid item xs={12} sm={6} md={3}>
    <Button
      fullWidth
      variant="outlined"
      onClick={onClick}
      sx={{
        minHeight: 116,
        borderRadius: 3,
        justifyContent: 'flex-start',
        textAlign: 'left',
        p: 2,
        bgcolor: '#fff',
      }}
    >
      <Box>
        <Typography sx={{ fontWeight: 900, color: '#132331' }}>{title}</Typography>
        <Typography sx={{ color: '#607083', fontSize: 13, mt: 0.5 }}>{description}</Typography>
      </Box>
    </Button>
  </Grid>
);

const TenantForm = ({ form, setForm, plans, apps, selectedTenant, onCreate, onUpdate, saving }) => (
  <Stack spacing={2}>
    <TextField label="Tenant ID (optional)" value={form.tenantId} onChange={(event) => setForm({ ...form, tenantId: event.target.value })} fullWidth />
    <TextField label="Tenant name" value={form.tenantName} onChange={(event) => setForm({ ...form, tenantName: event.target.value })} fullWidth />
    <FormControl fullWidth>
      <InputLabel>Pricing plan</InputLabel>
      <Select label="Pricing plan" value={form.planId} onChange={(event) => setForm({ ...form, planId: event.target.value })}>
        <MenuItem value="">No plan</MenuItem>
        {plans.map((plan) => <MenuItem value={plan.planId} key={plan.planId}>{plan.name}</MenuItem>)}
      </Select>
    </FormControl>
    <Grid container spacing={1}>
      <Grid item xs={6}>
        <FormControl fullWidth>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="suspended">Suspended</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <FormControl fullWidth>
          <InputLabel>Billing</InputLabel>
          <Select label="Billing" value={form.billingStatus} onChange={(event) => setForm({ ...form, billingStatus: event.target.value })}>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="trialing">Trialing</MenuItem>
            <MenuItem value="past_due">Past due</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
    <Grid container spacing={1}>
      {['monthlyCredits', 'purchasedCredits', 'usedCredits', 'remainingCredits', 'userLimit', 'activeUsers'].map((field) => (
        <Grid item xs={6} key={field}>
          <TextField
            label={field}
            type="number"
            value={form[field]}
            onChange={(event) => setForm({ ...form, [field]: event.target.value })}
            fullWidth
          />
        </Grid>
      ))}
    </Grid>
    <TextField
      label="Allowed apps"
      value={form.allowedApps}
      helperText={`Comma separated. Existing apps: ${apps.map((app) => app.appId).join(', ') || 'none yet'}`}
      onChange={(event) => setForm({ ...form, allowedApps: event.target.value })}
      fullWidth
    />
    <Stack direction="row" spacing={1}>
      <Button variant="contained" disabled={saving} onClick={onCreate}>Create</Button>
      <Button variant="outlined" disabled={saving || !selectedTenant} onClick={onUpdate}>Update Selected</Button>
      <Button variant="text" onClick={() => setForm(EMPTY_TENANT)}>Clear</Button>
    </Stack>
  </Stack>
);

const TenantControls = ({
  tenant,
  plans,
  apps,
  creditAmount,
  setCreditAmount,
  manualDeductAmount,
  setManualDeductAmount,
  saving,
  onAssignPlan,
  onStatus,
  onBilling,
  onAllowedApps,
  onUserLimit,
  onTopUp,
  onDeduct,
}) => {
  const [allowedApps, setAllowedApps] = useState('');
  const [userLimit, setUserLimit] = useState('');

  useEffect(() => {
    setAllowedApps((tenant?.allowedApps || []).join(', '));
    setUserLimit(tenant?.userLimit || '');
  }, [tenant]);

  if (!tenant) return <Alert severity="info">Create or select a tenant to manage controls.</Alert>;

  const usagePercent = tenant.monthlyCredits ? Math.min(100, (Number(tenant.remainingCredits || 0) / Number(tenant.monthlyCredits || 1)) * 100) : 0;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography sx={{ fontWeight: 800 }}>{tenant.tenantName}</Typography>
        <Typography sx={{ color: '#607083' }}>{tenant.tenantId}</Typography>
      </Box>
      <Box>
        <Stack direction="row" justifyContent="space-between">
          <Typography>Credits</Typography>
          <Typography sx={{ fontWeight: 800 }}>{formatNumber(tenant.remainingCredits)} / {formatNumber(tenant.monthlyCredits)}</Typography>
        </Stack>
        <LinearProgress variant="determinate" value={usagePercent} sx={{ mt: 1, height: 8, borderRadius: 99 }} />
      </Box>
      <FormControl fullWidth>
        <InputLabel>Assign plan</InputLabel>
        <Select label="Assign plan" value={tenant.planId || ''} onChange={(event) => onAssignPlan(event.target.value)}>
          {plans.map((plan) => <MenuItem value={plan.planId} key={plan.planId}>{plan.name}</MenuItem>)}
        </Select>
      </FormControl>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={tenant.status || 'inactive'} onChange={(event) => onStatus(event.target.value)}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <InputLabel>Billing</InputLabel>
            <Select label="Billing" value={tenant.billingStatus || 'cancelled'} onChange={(event) => onBilling(event.target.value)}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="trialing">Trialing</MenuItem>
              <MenuItem value="past_due">Past due</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <TextField
        label="Allowed apps"
        value={allowedApps}
        helperText={`Configured apps: ${apps.map((app) => app.appId).join(', ') || 'none yet'}`}
        onChange={(event) => setAllowedApps(event.target.value)}
      />
      <Button variant="outlined" disabled={saving} onClick={() => onAllowedApps(parseList(allowedApps))}>Save Allowed Apps</Button>
      <Stack direction="row" spacing={1}>
        <TextField label="User limit" type="number" value={userLimit} onChange={(event) => setUserLimit(event.target.value)} fullWidth />
        <Button variant="outlined" disabled={saving} onClick={() => onUserLimit(Number(userLimit || 0))}>Save</Button>
      </Stack>
      <Divider />
      <Stack direction="row" spacing={1}>
        <TextField label="Top-up credits" type="number" value={creditAmount} onChange={(event) => setCreditAmount(event.target.value)} fullWidth />
        <Button variant="contained" disabled={saving} onClick={onTopUp}>Top Up</Button>
      </Stack>
      <Stack direction="row" spacing={1}>
        <TextField label="Manual deduct" type="number" value={manualDeductAmount} onChange={(event) => setManualDeductAmount(event.target.value)} fullWidth />
        <Button color="warning" variant="outlined" disabled={saving} onClick={onDeduct}>Deduct</Button>
      </Stack>
    </Stack>
  );
};

const TenantTable = ({ rows, onEdit }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Tenant</TableCell>
          <TableCell>Plan</TableCell>
          <TableCell>Credits</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Billing</TableCell>
          <TableCell>Apps</TableCell>
          <TableCell align="right">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {!rows.length && <EmptyTableRow colSpan={7} message="No tenants yet." />}
        {rows.map((tenant) => (
          <TableRow key={tenant.tenantId} hover>
            <TableCell>
              <Typography sx={{ fontWeight: 800 }}>{tenant.tenantName}</Typography>
              <Typography variant="caption" sx={{ color: '#607083' }}>{tenant.tenantId}</Typography>
            </TableCell>
            <TableCell>{tenant.planId || '-'}</TableCell>
            <TableCell>{formatNumber(tenant.remainingCredits)} / {formatNumber(tenant.monthlyCredits)}</TableCell>
            <TableCell><Chip size="small" label={tenant.status} color={statusColors[tenant.status] || 'default'} /></TableCell>
            <TableCell><Chip size="small" label={tenant.billingStatus} color={statusColors[tenant.billingStatus] || 'default'} variant="outlined" /></TableCell>
            <TableCell>{(tenant.allowedApps || []).join(', ') || '-'}</TableCell>
            <TableCell align="right"><Button size="small" onClick={() => onEdit(tenant)}>Edit</Button></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const PlanForm = ({ form, setForm, apps, onSave, saving }) => (
  <Stack spacing={2}>
    <TextField label="Plan ID" value={form.planId} onChange={(event) => setForm({ ...form, planId: event.target.value })} fullWidth />
    <TextField label="Name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} fullWidth />
    <TextField label="Description" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} fullWidth multiline minRows={2} />
    <Grid container spacing={1}>
      <Grid item xs={4}><TextField label="Price" type="number" value={form.price} onChange={(event) => setForm({ ...form, price: event.target.value })} fullWidth /></Grid>
      <Grid item xs={4}><TextField label="Credits" type="number" value={form.monthlyCredits} onChange={(event) => setForm({ ...form, monthlyCredits: event.target.value })} fullWidth /></Grid>
      <Grid item xs={4}><TextField label="Users" type="number" value={form.userLimit} onChange={(event) => setForm({ ...form, userLimit: event.target.value })} fullWidth /></Grid>
    </Grid>
    <TextField label="Allowed apps" helperText={`Available apps: ${apps.map((app) => app.appId).join(', ') || 'none yet'}`} value={form.allowedApps} onChange={(event) => setForm({ ...form, allowedApps: event.target.value })} fullWidth />
    <TextField label="Feature access JSON" value={form.features} onChange={(event) => setForm({ ...form, features: event.target.value })} fullWidth multiline minRows={7} />
    <TextField label="Credit costs JSON" value={form.creditCosts} onChange={(event) => setForm({ ...form, creditCosts: event.target.value })} fullWidth multiline minRows={7} />
    <Button variant="contained" disabled={saving} onClick={onSave}>Save Plan</Button>
  </Stack>
);

const PricingPlanTable = ({ rows, onEdit, onStatus }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Plan</TableCell>
          <TableCell>Credits</TableCell>
          <TableCell>User limit</TableCell>
          <TableCell>Apps</TableCell>
          <TableCell>Status</TableCell>
          <TableCell align="right">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {!rows.length && <EmptyTableRow colSpan={6} message="No pricing plans yet." />}
        {rows.map((plan) => (
          <TableRow key={plan.planId} hover>
            <TableCell><Typography sx={{ fontWeight: 800 }}>{plan.name}</Typography><Typography variant="caption">{plan.planId}</Typography></TableCell>
            <TableCell>{formatNumber(plan.monthlyCredits)}</TableCell>
            <TableCell>{formatNumber(plan.userLimit)}</TableCell>
            <TableCell>{(plan.allowedApps || []).join(', ')}</TableCell>
            <TableCell><Chip size="small" label={plan.isActive === false ? 'inactive' : 'active'} color={plan.isActive === false ? 'default' : 'success'} /></TableCell>
            <TableCell align="right">
              <Button size="small" onClick={() => onEdit(plan)}>Edit</Button>
              <Button size="small" onClick={() => onStatus(plan)}>{plan.isActive === false ? 'Activate' : 'Deactivate'}</Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const AppForm = ({ form, setForm, onSave, saving }) => (
  <Stack spacing={2}>
    <TextField label="App ID" value={form.appId} onChange={(event) => setForm({ ...form, appId: event.target.value })} fullWidth />
    <TextField label="Name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} fullWidth />
    <TextField label="Description" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} fullWidth multiline minRows={2} />
    <TextField label="Actions JSON" value={form.actions} onChange={(event) => setForm({ ...form, actions: event.target.value })} fullWidth multiline minRows={10} />
    <Button variant="contained" disabled={saving} onClick={onSave}>Save Connected App</Button>
  </Stack>
);

const AppsTable = ({ rows, onEdit, onStatus }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>App</TableCell>
          <TableCell>Description</TableCell>
          <TableCell>Actions</TableCell>
          <TableCell>Status</TableCell>
          <TableCell align="right">Manage</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {!rows.length && <EmptyTableRow colSpan={5} message="No connected apps yet. Add ovplan1 here as configuration only." />}
        {rows.map((app) => (
          <TableRow key={app.appId} hover>
            <TableCell><Typography sx={{ fontWeight: 800 }}>{app.name}</Typography><Typography variant="caption">{app.appId}</Typography></TableCell>
            <TableCell>{app.description || '-'}</TableCell>
            <TableCell>{Object.keys(app.actions || {}).join(', ') || '-'}</TableCell>
            <TableCell><Chip size="small" label={app.isActive === false ? 'inactive' : 'active'} color={app.isActive === false ? 'default' : 'success'} /></TableCell>
            <TableCell align="right">
              <Button size="small" onClick={() => onEdit(app)}>Edit</Button>
              <Button size="small" onClick={() => onStatus(app)}>{app.isActive === false ? 'Activate' : 'Deactivate'}</Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const CreditUsageTable = ({ rows, compact = false }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Tenant</TableCell>
          {!compact && <TableCell>App</TableCell>}
          <TableCell>Action</TableCell>
          <TableCell>Credits</TableCell>
          {!compact && <TableCell>Performed by</TableCell>}
          {!compact && <TableCell>Reference</TableCell>}
          <TableCell>Status</TableCell>
          <TableCell>Created</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {!rows.length && <EmptyTableRow colSpan={compact ? 5 : 8} message="No credit usage yet." />}
        {rows.map((row) => (
          <TableRow key={row.id || `${row.tenantId}-${row.createdAt?._seconds || Math.random()}`}>
            <TableCell>{row.tenantId}</TableCell>
            {!compact && <TableCell>{row.appId}</TableCell>}
            <TableCell>{row.action}</TableCell>
            <TableCell>{formatNumber(row.creditsUsed)}</TableCell>
            {!compact && <TableCell>{row.performedBy || '-'}</TableCell>}
            {!compact && <TableCell>{row.referenceId || '-'}</TableCell>}
            <TableCell>
              <Chip size="small" label={row.status || '-'} color={row.status === 'success' ? 'success' : 'error'} />
              {row.failureReason ? <Typography variant="caption" sx={{ display: 'block' }}>{row.failureReason}</Typography> : null}
            </TableCell>
            <TableCell>{toDateLabel(row.createdAt)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const ApiClientsTable = ({ rows, onRevoke }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Client</TableCell>
          <TableCell>App</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Last used</TableCell>
          <TableCell align="right">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {!rows.length && <EmptyTableRow colSpan={5} message="No API clients yet." />}
        {rows.map((client) => (
          <TableRow key={client.clientId}>
            <TableCell><Typography sx={{ fontWeight: 800 }}>{client.name}</Typography><Typography variant="caption">{client.clientId}</Typography></TableCell>
            <TableCell>{client.appId}</TableCell>
            <TableCell><Chip size="small" label={client.status} color={statusColors[client.status] || 'default'} /></TableCell>
            <TableCell>{toDateLabel(client.lastUsedAt)}</TableCell>
            <TableCell align="right">
              <Button size="small" color="error" disabled={client.status === 'revoked'} onClick={() => onRevoke(client)}>Revoke</Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const AuditTable = ({ rows, compact = false }) => (
  <TableContainer>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Action</TableCell>
          <TableCell>Tenant</TableCell>
          {!compact && <TableCell>Performed by</TableCell>}
          {!compact && <TableCell>Metadata</TableCell>}
          <TableCell>Created</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {!rows.length && <EmptyTableRow colSpan={compact ? 3 : 5} message="No audit logs yet." />}
        {rows.map((row) => (
          <TableRow key={row.id}>
            <TableCell>{row.action}</TableCell>
            <TableCell>{row.tenantId || '-'}</TableCell>
            {!compact && <TableCell>{row.performedBy || '-'}</TableCell>}
            {!compact && <TableCell><Box component="code">{JSON.stringify(row.metadata || {})}</Box></TableCell>}
            <TableCell>{toDateLabel(row.createdAt)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const EmptyTableRow = ({ colSpan, message }) => (
  <TableRow>
    <TableCell colSpan={colSpan} sx={{ py: 4, textAlign: 'center', color: '#64748b' }}>
      {message}
    </TableCell>
  </TableRow>
);

export default TenantPricingConsole;
